// The new-customer form's behaviour (QA-323), apart from how it's drawn: Calls, Book and Messages draw it
// with NewCustomerForm; Customers in Blueprint with ui-web's NewCustomerFields (BL-189). It says so when
// the number, email or address is already a customer's (customers.possibleDuplicates, BL-029), but never
// blocks: a tenant or a new owner can share them (F-41). Saving checks again first and stops on a match
// until "Add anyway" (QA-699), so the warning is never missed for a field that wasn't left; "Try again"
// after a lost answer doesn't (QA-735).

import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { useSaveKey } from './save-key.ts';
import {
  checksFirst,
  dupeNotice,
  dupeQuery,
  emptyCustomerForm,
  newCustomerInput,
  type CustomerForm,
  type Shared,
} from './new-customer-model.ts';

export type Match = { id: string; displayName: string; same: Shared[] };

export interface NewCustomerOptions {
  /** A call's number (E.164): the form asks for no mobile then, and texting is asked on the call. */
  phone?: string;
  start?: CustomerForm;
  /** Who the duplicate notice is about ("this caller"). */
  who?: string;
  /** Runs once the customer exists (or an existing one is picked), before onDone: e.g. link the call. */
  link?: (customerId: string, idempotencyKey?: string) => Promise<unknown>;
  /** What picking the possible duplicate does: use them (book, link the call) or open them (Customers). */
  pick?: 'Use' | 'Open';
  onDone: (customerId: string) => void;
}

export function useNewCustomer({
  phone,
  start,
  who = 'them',
  link,
  pick = 'Use',
  onDone,
}: NewCustomerOptions) {
  const [f, setF] = useState<CustomerForm>(() => start ?? emptyCustomerForm(!!phone));
  const [dupe, setDupe] = useState<Match | null>(null);
  // saving stopped on the match (or on a check that couldn't run): "Add anyway" adds them
  const [held, setHeld] = useState<'match' | 'unchecked' | null>(null);
  const asked = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The same form, created again after a lost answer, keeps its key: the customer is added once.
  const save = useSaveKey(JSON.stringify(f));
  // the key of the last try that reached customers.create, whatever its answer (QA-735)
  const sent = useRef<string | null>(null);
  const change = (patch: Partial<CustomerForm>) => {
    setF({ ...f, ...patch });
    setError(null); // a refusal is about what was there; once it's edited, it no longer stands
  };

  // Everything typed so far, matched exactly (the same number, email, or street and city): the most
  // shared. Only the latest check's answer is shown; undefined when the check couldn't run.
  const check = async (form: CustomerForm = f): Promise<Match | null | undefined> => {
    const n = ++asked.current;
    const q = dupeQuery(form, phone);
    const r = q
      ? await client.call<{ matches: Match[] }>('customers.possibleDuplicates', q).catch(() => undefined)
      : { matches: [] };
    if (n !== asked.current || !r) return r ? (r.matches[0] ?? null) : undefined;
    setDupe(r.matches[0] ?? null);
    if (!r.matches[0]) setHeld(null);
    return r.matches[0] ?? null;
  };
  // a call's number is known from the start: say at once if it's already a customer's
  useEffect(() => {
    if (phone) void check();
  }, [phone]);

  const create = async (anyway = false) => {
    setError(null);
    const made = newCustomerInput(f, { ...(phone ? { phone } : {}), country: 'CA' });
    if ('error' in made) return setError(made.error);
    setBusy(true);
    try {
      // QA-699: never added past a possible duplicate without saying so, whatever was left or not;
      // a retry of a try that reached the server goes straight to it (QA-735)
      if (checksFirst({ anyway, key: save.key, sentKey: sent.current })) {
        const m = await check();
        if (m !== null) {
          setHeld(m ? 'match' : 'unchecked');
          return;
        }
      }
      setHeld(null);
      sent.current = save.key;
      const c = await client.call<{ id: string }>('customers.create', made.input, {
        idempotencyKey: save.key,
      });
      await link?.(c.id, `${save.key}:link`);
      onDone(c.id);
    } catch (e) {
      // A refusal says why; a lost answer may have landed, and the held key makes a retry safe.
      const lost = !(e instanceof RpcError) || e.code === 'offline' || e.code === 'internal';
      setError(lost ? 'Couldn’t add the customer. Try again; they won’t be added twice.' : e.message);
    } finally {
      setBusy(false);
    }
  };

  return {
    form: f,
    change,
    check: () => void check(),
    create: (anyway = false) => void create(anyway),
    busy,
    error,
    /** Nothing to add yet: no name, or a street without its city. */
    blocked: !f.name.trim() || (!!f.line1.trim() && !f.city.trim()),
    held,
    /** The possible duplicate, in words, and picking them. */
    dupe: dupe
      ? {
          match: dupe,
          text: dupeNotice(dupe, { fromCall: !!phone, who, held: held !== null, pick }),
          pickLabel: `${pick} ${dupe.displayName}`,
          pick: () => void Promise.resolve(link?.(dupe.id)).then(() => onDone(dupe.id)),
        }
      : null,
    /** The check couldn't run and saving stopped on that. */
    uncheckedText:
      held === 'unchecked' && !dupe
        ? `Couldn’t check whether ${who === 'them' ? 'they are' : `${who} is`} already a customer. Add anyway?`
        : null,
  };
}
